import { motion } from 'framer-motion';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Shield } from 'lucide-react';

const PrivacyPolicy = () => {
  const lastUpdated = 'October 29, 2026';

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Header */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-6">
                <Shield className="w-8 h-8 text-primary" />
              </div>
              <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">Privacy Policy</h1>
              <p className="text-muted-foreground">Last Updated: {lastUpdated}</p>
            </div>

            {/* Content */}
            <div className="bg-white rounded-3xl p-8 md:p-12 border-2 border-border prose prose-lg max-w-none">
              <section className="mb-8">
                <h2 className="text-2xl font-heading font-bold mb-4">1. Who we are</h2>
                <p className="text-muted-foreground mb-4">
                  ExpatsSphere is a social platform for the global expat community operated by
                  <strong> Medpax, LLC</strong>, a company registered in the United States. Medpax, LLC is
                  responsible for the personal information described in this policy. This policy applies
                  when you use the ExpatsSphere website and mobile apps.
                </p>
                <p className="text-muted-foreground">
                  Questions or privacy requests may be sent to
                  {' '}<a href="mailto:Support@expatssphere.com" className="text-primary underline">Support@expatssphere.com</a>{' '}
                  or through{' '}
                  <a href="https://expatssphere.com/contact" className="text-primary underline">https://expatssphere.com/contact</a>.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-bold mb-4">2. Information we collect</h2>
                <ul className="list-disc pl-6 text-muted-foreground space-y-3">
                  <li>
                    <strong>Account information.</strong> When you create an account, we collect your name,
                    email address, nationality, and current country of residence. We use email verification to
                    help confirm accounts. We do not ask members to submit government-issued identification or
                    passports for verification.
                  </li>
                  <li>
                    <strong>Optional profile information.</strong> You may add a profile picture, birth city,
                    interests, and work experience.
                  </li>
                  <li>
                    <strong>Activity and content.</strong> When you use social features, we process the
                    information you choose to share, such as posts, comments, group participation, event
                    activity, connections, and communications through the platform, where those features are
                    available. Other members may see information you make visible through your profile, groups,
                    events, or posts. Please consider what you share, including information about other people.
                  </li>
                  <li>
                    <strong>Technical and approximate location information.</strong> We process IP addresses and
                    approximate location information to operate and protect the service and to help you find
                    relevant local members and events. We do not describe approximate location as precise GPS
                    location unless a feature separately requests and uses precise location.
                  </li>
                  <li>
                    <strong>Communications with us.</strong> If you contact us, we receive the information you
                    provide in your message and the contact details needed to respond.
                  </li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-bold mb-4">3. How we use information</h2>
                <p className="text-muted-foreground mb-4">
                  We use personal information to create and maintain accounts; enable member profiles,
                  connections, groups, events, and communications; recommend relevant events, groups, members,
                  and local resources; verify email addresses; respond to requests; and review reports, moderate
                  content, and protect members and the service.
                </p>
                <p className="text-muted-foreground">
                  We may send service messages needed to operate your account. If we offer optional marketing
                  emails, you can opt out using the instructions in those emails or by contacting us.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-bold mb-4">4. Visibility and sharing</h2>
                <p className="text-muted-foreground mb-4">
                  ExpatsSphere is a community service. Information you choose to publish or share may be visible
                  to other members according to the feature you use. Members who have seen your information may
                  have copied or shared it outside ExpatsSphere; deleting an account cannot erase copies outside
                  our control.
                </p>
                <p className="text-muted-foreground">
                  We do not sell, rent, or trade members' personal information. We have no data-sharing
                  partnerships. We may disclose information when required by applicable law or when necessary to
                  address a valid legal request or protect the rights and safety of members and others, subject
                  to applicable law.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-bold mb-4">5. Account deletion and retention</h2>
                <p className="text-muted-foreground mb-4">
                  You can delete your account from <strong>Profile → Privacy Settings</strong>. Account deletion
                  is immediate and permanent. There is no restore window, and we do not retain deleted account
                  data in backups.
                </p>
                <p className="text-muted-foreground mb-4">
                  When you delete your account, we permanently delete your user record and associated
                  information in one deletion transaction, including:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground space-y-2">
                  <li>Events you created, without placing them in the 30-day event trash;</li>
                  <li>Jobs, properties, marketplace items, and business listings you created;</li>
                  <li>Messages and conversations, including the copies visible to the other participants;</li>
                  <li>Push subscriptions and profile-view records;</li>
                  <li>Reports you submitted and reports about you; and</li>
                  <li>Your contacts and connections, including entries on other members' profiles.</li>
                </ul>
                <p className="text-muted-foreground mt-4">
                  Other members may have independently copied, downloaded, or shared information outside
                  ExpatsSphere before deletion. We cannot delete those outside copies.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-bold mb-4">6. Security</h2>
                <p className="text-muted-foreground">
                  We use reasonable measures designed to protect personal information against unauthorized
                  access, loss, or misuse. No online service can guarantee absolute security. Please use a
                  strong password and contact us if you believe your account has been compromised.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-bold mb-4">7. Privacy rights and choices</h2>
                <p className="text-muted-foreground mb-4">
                  You can request access to the personal information we hold about you, ask us to correct
                  inaccurate information, or delete your account. You can also opt out of optional marketing
                  communications. Send requests to
                  {' '}<a href="mailto:Support@expatssphere.com" className="text-primary underline">Support@expatssphere.com</a>.
                  We may need to verify that a request comes from the account holder before acting on it.
                </p>
                <p className="text-muted-foreground mb-4">
                  If privacy laws in your location give you additional rights, such as the right to receive a
                  copy of your data, object to or restrict certain processing, or withdraw consent where consent
                  is the basis for processing, you may exercise those rights by contacting us. EU and EEA users
                  may also complain to a competent data protection authority. Rights can be subject to legal
                  exceptions.
                </p>
                <p className="text-muted-foreground">
                  For users in the EU or EEA, the legal bases for processing include performing our agreement
                  with you to provide your account and community features; our legitimate interests in
                  maintaining a safe and functional service and responding to requests, where those interests
                  are not overridden by your rights; compliance with legal obligations; and consent where we
                  specifically ask for it, such as for optional marketing where required. You may withdraw
                  consent at any time without affecting processing already carried out lawfully.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-bold mb-4">8. Children</h2>
                <p className="text-muted-foreground">
                  ExpatsSphere is not intended for children under 18. If we learn that an ineligible child has
                  created an account, we will delete that account and its associated personal information.
                  Contact us if you believe a child below the applicable age has an account.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-bold mb-4">9. Changes to this policy</h2>
                <p className="text-muted-foreground">
                  We may update this policy when our practices or legal requirements change. We will post the
                  revised policy with a new "Last updated" date and provide additional notice if required by
                  applicable law.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-heading font-bold mb-4">10. Contact</h2>
                <p className="text-muted-foreground mb-2">
                  <strong>Medpax, LLC</strong> (operator of ExpatsSphere)
                </p>
                <p className="text-muted-foreground">
                  <strong>Email:</strong>{' '}
                  <a href="mailto:Support@expatssphere.com" className="text-primary underline">Support@expatssphere.com</a><br />
                  <strong>Contact form:</strong>{' '}
                  <a href="https://expatssphere.com/contact" className="text-primary underline">https://expatssphere.com/contact</a>
                </p>
              </section>
            </div>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
